import React, { useState, useRef } from "react";
import {
  IconMail,
  IconFileText,
  IconSignature,
  IconDownload,
  IconPlus,
  IconSearch,
  IconFilter,
  IconCheck,
  IconCopy,
  IconRotate,
  IconList,
  IconEye,
  IconEdit,
  IconTrash,
  IconDotsVertical,
  IconArrowLeft,
  IconChevronLeft,
  IconChevronRight,
  IconBold,
  IconItalic,
  IconUnderline,
  IconAlignLeft,
  IconAlignCenter,
  IconAlignRight,
  IconAlignJustified,
  IconListNumbers,
  IconLink,
  IconPhoto,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconSettings
} from "@tabler/icons-react";
import RoleToggle from "../../../components/RoleToggle";


// Mock data for Candidate Offer Documents (Tab 1)
const INITIAL_OFFERS = [
  { id: "OFFER-101", candidate: "Rohan Das", email: "rohan.das@example.com", position: "DevOps Engineer", template: "Standard Technical Offer", status: "Accepted", sentDate: "May 15, 2026", country: "India" },
  { id: "OFFER-102", candidate: "Sneha Nair", email: "sneha.nair@example.com", position: "Senior React Developer", template: "Standard Technical Offer", status: "Sent (e-sign)", sentDate: "May 18, 2026", country: "India" },
  { id: "OFFER-103", candidate: "John Doe", email: "john.doe@example.com", position: "Sales Exec", template: "International Sales Offer", status: "Draft", sentDate: "N/A", country: "United States" },
];

interface Template {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  language: string;
  properties: {
    addHeader?: boolean;
    addBorder?: boolean;
    addLetterHead?: boolean;
    addSignatureLines?: boolean;
    enableCompanyWatermark?: boolean;
    enableFooter?: boolean;
  };
}

// Mock data for Letter Templates (Tab 2)
const INITIAL_TEMPLATES: Template[] = [
  { id: "LET#004", title: "Test", createdAt: "29-01-2026", updatedAt: "29-01-2026", language: "Bilingual", properties: { addHeader: true, addBorder: true } },
  { id: "LET#003", title: "Offer Letter", createdAt: "01-12-2025", updatedAt: "08-04-2026", language: "Bilingual", properties: { addLetterHead: true, addSignatureLines: true } },
  { id: "LET#002", title: "Offer Letter 7", createdAt: "01-12-2025", updatedAt: "14-03-2026", language: "English Only", properties: { addBorder: true } },
  { id: "LET#001", title: "Appraisal Letter", createdAt: "01-12-2025", updatedAt: "01-12-2025", language: "English Only", properties: {} },
  { id: "Temp#017", title: "Offer Letter 1", createdAt: "01-12-2025", updatedAt: "01-12-2025", language: "Bilingual", properties: {} },
  { id: "Temp#001", title: "Offer Letter 6", createdAt: "27-11-2025", updatedAt: "01-12-2025", language: "Arabic Only", properties: {} },
  { id: "Temp#002", title: "Appraisal Letter", createdAt: "27-11-2025", updatedAt: "27-11-2025", language: "English Only", properties: {} },
  { id: "Temp#001-5", title: "Offer Letter 5", createdAt: "27-11-2025", updatedAt: "01-12-2025", language: "Bilingual", properties: {} },
  { id: "Temp#002-2", title: "Appraisal Letter", createdAt: "27-11-2025", updatedAt: "13-12-2025", language: "English Only", properties: {} },
  { id: "Temp#001-4", title: "Offer Letter 4", createdAt: "25-11-2025", updatedAt: "01-12-2025", language: "Bilingual", properties: {} }
];

// Available placeholder variables
const VARIABLES_LIST = [
  { name: "NAME", desc: "Candidate Name" },
  { name: "PERSONAL_EMAIL", desc: "Candidate Email Address" },
  { name: "PAN_NUMBER", desc: "Indian Income Tax Permanent Account Number" },
  { name: "ADHAR_NUMBER", desc: "Indian Unique Identification Number" },
  { name: "MOBILE_NUMBER", desc: "Contact phone number" },
  { name: "JOINING_DATE", desc: "Official date of joining" },
  { name: "PROBATION_END_DATE", desc: "Scheduled end date of probation" },
  { name: "NOTICE_PERIOD_END_DATE", desc: "Expected final working day" },
  { name: "DEPARTMENT", desc: "Assigned organizational department" },
  { name: "DESIGNATION", desc: "Job title / position" },
  { name: "GOSI_NUMBER", desc: "Saudi Social Insurance Number" },
  { name: "IQAMA_ID", desc: "Saudi Resident Identification ID" }
];

export default function Letter() {
  const [activeTab, setActiveTab] = useState<"created" | "templates">("created");

  const subViewTabs = [
    "Documents Created",
    "Letter Templates"
  ];
  const tabToViewMap: Record<string, "created" | "templates"> = {
    "Documents Created": "created",
    "Letter Templates": "templates"
  };
  const viewToTabMap: Record<string, string> = {
    created: "Documents Created",
    templates: "Letter Templates"
  };
  
  // Document Created States (Tab 1)
  const [offers, setOffers] = useState(INITIAL_OFFERS);
  const [searchCreated, setSearchCreated] = useState("");

  // Letter Templates States (Tab 2)
  const [templates, setTemplates] = useState<Template[]>(INITIAL_TEMPLATES);
  const [searchTemplate, setSearchTemplate] = useState("");
  const [showCreateEditor, setShowCreateEditor] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);

  // Editor configuration states
  const [templateTitle, setTemplateTitle] = useState("");
  const [paperSize, setPaperSize] = useState<"A4" | "A3">("A4");
  const [editorLang, setEditorLang] = useState<"english" | "arabic" | "bilingual">("bilingual");
  
  const [propSignature, setPropSignature] = useState(true);
  const [propWatermark, setPropWatermark] = useState(false);
  const [propBorder, setPropBorder] = useState(true);
  const [propHeader, setPropHeader] = useState(false);
  const [propLetterHead, setPropLetterHead] = useState(true);
  const [propFooter, setPropFooter] = useState(true);

  const [editorRightTab, setEditorRightTab] = useState<"properties" | "variables">("properties");
  const [fontSize, setFontSize] = useState(14);
  const [searchVar, setSearchVar] = useState("");
  const [toastMessage, setToastMessage] = useState("");

  // Ref handles for raw content extraction
  const contentRefEng = useRef<HTMLDivElement>(null);
  const contentRefAr = useRef<HTMLDivElement>(null);

  // Pagination for templates
  const [templatePage, setTemplatePage] = useState(1);
  const recordsPerPage = 10;

  // Show Toast helper
  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage("");
    }, 3000);
  };

  // Copy to clipboard helper
  const handleCopyVariable = (name: string) => {
    const textToCopy = `{{${name}}}`;
    navigator.clipboard.writeText(textToCopy)
      .then(() => triggerToast(`Copied ${textToCopy} to clipboard!`))
      .catch(() => triggerToast("Failed to copy variable."));
  };

  // Trigger Rich Editor ExecCommand
  const handleEditorAction = (cmd: string, val: string = "") => {
    document.execCommand(cmd, false, val);
  };

  // Navigate to Template Editor
  const handleAddTemplateClick = () => {
    setEditingTemplateId(null);
    setTemplateTitle("");
    setEditorLang("bilingual");
    setPaperSize("A4");
    setPropSignature(true);
    setPropWatermark(false);
    setPropBorder(true);
    setPropHeader(false);
    setPropLetterHead(true);
    setPropFooter(true);
    setShowCreateEditor(true);
  };

  const handleEditTemplate = (tmpl: typeof INITIAL_TEMPLATES[0]) => {
    setEditingTemplateId(tmpl.id);
    setTemplateTitle(tmpl.title);
    setEditorLang(tmpl.language === "Bilingual" ? "bilingual" : tmpl.language === "Arabic Only" ? "arabic" : "english");
    setPropSignature(!!tmpl.properties?.addSignatureLines);
    setPropWatermark(!!tmpl.properties?.enableCompanyWatermark);
    setPropBorder(!!tmpl.properties?.addBorder);
    setPropHeader(!!tmpl.properties?.addHeader);
    setPropLetterHead(!!tmpl.properties?.addLetterHead);
    setPropFooter(!!tmpl.properties?.enableFooter);
    setShowCreateEditor(true);
  };

  // Save new/edited template
  const handleSaveTemplate = () => {
    if (!templateTitle.trim()) {
      triggerToast("Please enter a Template Title.");
      return;
    }

    const todayStr = new Date().toLocaleDateString("en-GB").replace(/\//g, "-");

    if (editingTemplateId) {
      // Edit mode
      setTemplates(prev => prev.map(t => t.id === editingTemplateId ? {
        ...t,
        title: templateTitle,
        updatedAt: todayStr,
        language: editorLang === "bilingual" ? "Bilingual" : editorLang === "arabic" ? "Arabic Only" : "English Only",
        properties: {
          addSignatureLines: propSignature,
          enableCompanyWatermark: propWatermark,
          addBorder: propBorder,
          addHeader: propHeader,
          addLetterHead: propLetterHead,
          enableFooter: propFooter
        }
      } : t));
      triggerToast(`Template "${templateTitle}" updated successfully!`);
    } else {
      // Add Mode
      const newId = `LET#${String(templates.length + 1).padStart(3, "0")}`;
      const newTmpl = {
        id: newId,
        title: templateTitle,
        createdAt: todayStr,
        updatedAt: todayStr,
        language: editorLang === "bilingual" ? "Bilingual" : editorLang === "arabic" ? "Arabic Only" : "English Only",
        properties: {
          addSignatureLines: propSignature,
          enableCompanyWatermark: propWatermark,
          addBorder: propBorder,
          addHeader: propHeader,
          addLetterHead: propLetterHead,
          enableFooter: propFooter
        }
      };
      setTemplates([newTmpl, ...templates]);
      triggerToast(`Template "${templateTitle}" created successfully!`);
    }

    setShowCreateEditor(false);
  };

  // Delete Template
  const handleDeleteTemplate = (id: string) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      setTemplates(prev => prev.filter(t => t.id !== id));
      triggerToast("Template deleted successfully.");
    }
  };

  // Filter handlers
  const filteredOffers = offers.filter(o =>
    o.candidate.toLowerCase().includes(searchCreated.toLowerCase()) ||
    o.position.toLowerCase().includes(searchCreated.toLowerCase())
  );

  const filteredTemplates = templates.filter(t =>
    t.title.toLowerCase().includes(searchTemplate.toLowerCase()) ||
    t.id.toLowerCase().includes(searchTemplate.toLowerCase())
  );

  // Pagination bounds for templates table
  const totalPages = Math.ceil(filteredTemplates.length / recordsPerPage);
  const paginatedTemplates = filteredTemplates.slice(
    (templatePage - 1) * recordsPerPage,
    templatePage * recordsPerPage
  );

  const filteredVars = VARIABLES_LIST.filter(v =>
    v.name.toLowerCase().includes(searchVar.toLowerCase()) ||
    v.desc.toLowerCase().includes(searchVar.toLowerCase())
  );

  return (
    <div className="w-full mx-auto mt-2 animate-in fade-in duration-300 relative">
      
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-4 right-4 bg-[#212121] text-white px-4 py-3 rounded-xl shadow-xl flex items-center gap-2 border border-gray-800 z-50 animate-bounce">
          <IconCheck size={18} className="text-[#FBD037]" />
          <span className="text-xs font-bold font-mono">{toastMessage}</span>
        </div>
      )}

      {/* Main Breadcrumb Header (when list view is active) */}
      {!showCreateEditor && (
        <div className="flex items-center justify-between mb-4 px-2">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
              <span>Dashboard</span>
              <span>&gt;</span>
              <span className="text-[#212121]">{activeTab === "created" ? "Document Created" : "Letter Template"}</span>
            </div>
            <h1 className="text-2xl font-black text-[#212121]">
              {activeTab === "created" ? "Document Generated Repository" : "Letter Template Builder"}
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {activeTab === "created"
                ? "Track created contracts, compliance letters, and candidate audit logs"
                : "Manage bilingual Saudi Arabia contracts and customizable template forms"}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {activeTab === "templates" && (
              <button
                onClick={handleAddTemplateClick}
                className="bg-[#FBD037] text-[#212121] px-4 py-2.5 rounded-xl flex items-center gap-2 text-xs font-bold hover:bg-[#FACC15] transition shadow-sm"
              >
                <IconPlus size={16} /> Add Letter Template
              </button>
            )}
          </div>
        </div>
      )}
      {/* Toggle Tabs (only when not inside the editor) */}
      {!showCreateEditor && (
        <div className="flex items-center justify-between mb-6 px-2">
          <RoleToggle
            options={subViewTabs}
            active={viewToTabMap[activeTab]}
            onChange={(tab) => {
              setActiveTab(tabToViewMap[tab]);
              if (tabToViewMap[tab] === "templates") {
                setTemplatePage(1);
              }
            }}
          />
        </div>
      )}

      {/* ======================= TAB 1: DOCUMENTS CREATED ======================= */}
      {activeTab === "created" && !showCreateEditor && (
        <>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4 px-2">
            <div className="flex items-center gap-3">
              <div className="relative">
                <IconSearch size={16} className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search candidate..."
                  value={searchCreated}
                  onChange={e => setSearchCreated(e.target.value)}
                  className="pl-9 pr-4 py-2 rounded-xl border border-gray-200 bg-white text-xs outline-none focus:border-[#FBD037] w-64 shadow-sm font-sans"
                />
              </div>
              <button className="border border-gray-200 bg-white text-gray-700 hover:border-[#FBD037] hover:bg-[#FBD037]/10 px-4 py-2 rounded-xl flex items-center gap-2 text-xs font-bold transition shadow-sm">
                <IconFilter size={16} /> Filter
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-gray-150 bg-[#F7F6F2]">
                    <th className="px-6 py-3.5 font-bold text-[#212121]">Offer Ref</th>
                    <th className="px-6 py-3.5 font-bold text-[#212121]">Candidate</th>
                    <th className="px-6 py-3.5 font-bold text-[#212121]">Position</th>
                    <th className="px-6 py-3.5 font-bold text-[#212121]">Country Clauses</th>
                    <th className="px-6 py-3.5 font-bold text-[#212121]">Status</th>
                    <th className="px-6 py-3.5 font-bold text-[#212121]">Sent Date</th>
                    <th className="px-6 py-3.5 font-bold text-[#212121] text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOffers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-10 text-gray-400 font-medium">No generated documents match search.</td>
                    </tr>
                  ) : (
                    filteredOffers.map((item) => (
                      <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="px-6 py-4 font-bold text-gray-900 font-mono">{item.id}</td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-bold text-gray-800">{item.candidate}</div>
                            <div className="text-[10px] text-gray-400 font-mono">{item.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-650 font-medium">{item.position}</td>
                        <td className="px-6 py-4">
                          <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-gray-600 font-bold tracking-wide uppercase">{item.country}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold ${
                            item.status === "Accepted" ? "bg-emerald-50 text-emerald-600 border border-emerald-100" :
                            item.status === "Draft" ? "bg-gray-100 text-gray-500 border border-gray-200" :
                            "bg-amber-50 text-amber-600 border border-amber-100"
                          }`}>
                            {item.status === "Accepted" && <IconCheck size={12} />}
                            {item.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-400 font-medium font-mono">{item.sentDate}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:border-[#FBD037] hover:bg-[#FBD037]/10 transition shadow-sm">
                              <IconMail size={14} />
                            </button>
                            <button className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:border-[#FBD037] hover:bg-[#FBD037]/10 transition shadow-sm">
                              <IconDownload size={14} />
                            </button>
                            {item.status === "Accepted" && (
                              <button className="text-[9px] bg-black text-[#FBD037] hover:bg-gray-900 font-bold px-2 py-1 rounded transition shadow-sm">
                                Onboard
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ======================= TAB 2: LETTER TEMPLATES (LIST) ======================= */}
      {activeTab === "templates" && !showCreateEditor && (
        <>
          {/* Toolbar according to mockup */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4 px-2">
            <div className="flex items-center gap-3">
              <div className="relative">
                <IconSearch size={16} className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search template title..."
                  value={searchTemplate}
                  onChange={e => setSearchTemplate(e.target.value)}
                  className="pl-9 pr-4 py-2 rounded-xl border border-gray-200 bg-white text-xs outline-none focus:border-[#FBD037] w-64 shadow-sm font-sans"
                />
              </div>
              <button className="border border-gray-200 bg-white text-gray-700 hover:border-[#FBD037] hover:bg-[#FBD037]/10 px-4 py-2 rounded-xl flex items-center gap-2 text-xs font-bold transition shadow-sm">
                <IconFilter size={16} /> Filter <span className="text-[9px] text-gray-400">▼</span>
              </button>
            </div>
            
            {/* Action Switchers & Layout Toggles */}
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setSearchTemplate("")} 
                className="p-2 border border-gray-200 hover:border-gray-300 bg-white text-gray-600 rounded-xl transition shadow-sm"
                title="Reset View"
              >
                <IconRotate size={15} />
              </button>
              <div className="flex border border-gray-200 bg-white rounded-xl overflow-hidden p-0.5 items-center">
                <button className="p-1.5 bg-[#FBD037] text-gray-900 rounded-lg" title="List Layout">
                  <IconList size={14} />
                </button>
                <div className="w-6 h-4 bg-gray-100 mx-1 rounded flex items-center justify-start p-0.5 cursor-pointer">
                  <span className="w-2.5 h-2.5 bg-gray-400 rounded-full"></span>
                </div>
              </div>
            </div>
          </div>

          {/* Table representing Image 1 */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden relative">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-gray-150 bg-[#F7F6F2]">
                    <th className="px-4 py-3 text-center w-10">
                      <input type="checkbox" className="rounded accent-[#FBD037]" />
                    </th>
                    <th className="px-4 py-3 font-bold text-gray-500 uppercase tracking-wider w-16">SR NO.</th>
                    <th className="px-6 py-3 font-bold text-[#212121] uppercase tracking-wider">Letter ID</th>
                    <th className="px-6 py-3 font-bold text-[#212121] uppercase tracking-wider">Letter Title</th>
                    <th className="px-6 py-3 font-bold text-[#212121] uppercase tracking-wider">Created At</th>
                    <th className="px-6 py-3 font-bold text-[#212121] uppercase tracking-wider">Updated At</th>
                    <th className="px-6 py-3 font-bold text-[#212121] uppercase tracking-wider">Language</th>
                    <th className="px-6 py-3 font-bold text-[#212121] uppercase tracking-wider text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTemplates.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-10 text-gray-400 font-medium">No templates matching description.</td>
                    </tr>
                  ) : (
                    paginatedTemplates.map((item, idx) => (
                      <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="px-4 py-4 text-center">
                          <input type="checkbox" className="rounded accent-[#FBD037]" />
                        </td>
                        <td className="px-4 py-4 text-gray-400 font-bold font-mono">{(templatePage - 1) * recordsPerPage + idx + 1}</td>
                        <td className="px-6 py-4 font-bold text-[#212121] font-mono">{item.id}</td>
                        <td className="px-6 py-4 font-bold text-gray-800 hover:text-[#007BFF] cursor-pointer" onClick={() => handleEditTemplate(item)}>
                          {item.title}
                        </td>
                        <td className="px-6 py-4 text-gray-500 font-medium font-mono">{item.createdAt}</td>
                        <td className="px-6 py-4 text-gray-500 font-medium font-mono">{item.updatedAt}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            item.language === "Bilingual" ? "bg-amber-50 text-amber-600 border border-amber-100" :
                            item.language === "Arabic Only" ? "bg-emerald-50 text-emerald-600 border border-emerald-100" :
                            "bg-blue-50 text-blue-600 border border-blue-100"
                          }`}>
                            {item.language}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              onClick={() => handleEditTemplate(item)}
                              className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:border-[#FBD037] hover:bg-[#FBD037]/10 transition"
                              title="Edit Template"
                            >
                              <IconEdit size={13} />
                            </button>
                            <button
                              onClick={() => handleDeleteTemplate(item.id)}
                              className="p-1.5 rounded-lg border border-gray-200 text-red-500 hover:border-red-400 hover:bg-red-50 transition"
                              title="Delete Template"
                            >
                              <IconTrash size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Custom Pagination Layout from Image 1 */}
            <div className="flex items-center justify-between border-t border-gray-150 px-6 py-3 text-xs bg-gray-50">
              <div className="text-gray-500 font-medium">
                Record {(templatePage - 1) * recordsPerPage + 1}-{Math.min(templatePage * recordsPerPage, filteredTemplates.length)} of {filteredTemplates.length}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-gray-500">
                  <span>Page size:</span>
                  <select className="border border-gray-200 rounded p-1 bg-white text-xs font-bold outline-none">
                    <option value="10">10</option>
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setTemplatePage(p => Math.max(p - 1, 1))}
                    disabled={templatePage === 1}
                    className="p-1.5 border border-gray-200 bg-white rounded-lg hover:bg-gray-150 disabled:opacity-50 transition"
                  >
                    <IconChevronLeft size={13} />
                  </button>
                  <span className="font-mono font-bold text-gray-800">{templatePage} / {totalPages || 1}</span>
                  <button
                    onClick={() => setTemplatePage(p => Math.min(p + 1, totalPages))}
                    disabled={templatePage === totalPages || totalPages === 0}
                    className="p-1.5 border border-gray-200 bg-white rounded-lg hover:bg-gray-150 disabled:opacity-50 transition"
                  >
                    <IconChevronRight size={13} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ======================= LETTERS TEMPLATE CREATOR / EDITOR ======================= */}
      {showCreateEditor && (
        <div className="animate-in fade-in duration-300">
          
          {/* Breadcrumb Editor Header */}
          <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-4 px-2">
            <div>
              <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                <span className="cursor-pointer hover:text-gray-600" onClick={() => setShowCreateEditor(false)}>Dashboard</span>
                <span>&gt;</span>
                <span className="cursor-pointer hover:text-gray-600" onClick={() => setShowCreateEditor(false)}>Letter Template</span>
                <span>&gt;</span>
                <span className="text-[#212121]">{editingTemplateId ? "Edit" : "Create"}</span>
              </div>
              
              {/* Dynamic Design Title Input */}
              <div className="flex items-center gap-2 mt-1">
                <span className="p-1.5 bg-[#FBD037] text-gray-900 rounded-lg">
                  <IconFileText size={18} />
                </span>
                <input
                  type="text"
                  required
                  placeholder="Enter Template Title..."
                  value={templateTitle}
                  onChange={e => setTemplateTitle(e.target.value)}
                  className="text-lg font-black text-gray-850 outline-none border-b-2 border-transparent focus:border-[#FBD037] bg-transparent pb-0.5 w-80"
                />
              </div>
            </div>

            {/* Top Right Save */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCreateEditor(false)}
                className="px-4 py-2 border border-gray-200 bg-white font-bold rounded-xl text-xs hover:bg-gray-50 transition text-gray-700"
              >
                Back to List
              </button>
              <button
                onClick={handleSaveTemplate}
                className="bg-black text-[#FBD037] hover:bg-gray-900 font-bold px-4 py-2 rounded-xl text-xs transition shadow-md"
              >
                Save Template
              </button>
            </div>
          </div>

          {/* Formatting Toolbar */}
          <div className="bg-white border border-gray-200 rounded-xl p-2 mb-4 flex flex-wrap gap-1.5 items-center justify-between shadow-xs">
            <div className="flex flex-wrap gap-1 items-center">
              
              {/* Paper Size Selector (A4 / A3 Toggle) */}
              <div className="flex bg-gray-100 rounded-lg p-0.5 font-mono text-[9px] font-black mr-2">
                <button
                  type="button"
                  onClick={() => setPaperSize("A4")}
                  className={`px-2 py-1 rounded-md transition ${
                    paperSize === "A4" ? "bg-black text-[#FBD037]" : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  A4
                </button>
                <button
                  type="button"
                  onClick={() => setPaperSize("A3")}
                  className={`px-2 py-1 rounded-md transition ${
                    paperSize === "A3" ? "bg-black text-[#FBD037]" : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  A3
                </button>
              </div>

              <span className="w-px h-5 bg-gray-200 mx-1"></span>

              {/* Font Size Adjuster */}
              <div className="flex items-center gap-1 mr-2 text-[11px] font-bold">
                <button
                  onClick={() => setFontSize(f => Math.max(f - 1, 10))}
                  className="w-5 h-5 rounded hover:bg-gray-100 border border-gray-200 flex items-center justify-center"
                >
                  -
                </button>
                <span className="w-8 text-center font-mono">{fontSize}px</span>
                <button
                  onClick={() => setFontSize(f => Math.min(f + 1, 28))}
                  className="w-5 h-5 rounded hover:bg-gray-100 border border-gray-200 flex items-center justify-center"
                >
                  +
                </button>
              </div>

              <span className="w-px h-5 bg-gray-200 mx-1"></span>

              {/* Standard Formatting Options */}
              <button
                type="button"
                onClick={() => handleEditorAction("bold")}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                title="Bold"
              >
                <IconBold size={15} />
              </button>
              <button
                type="button"
                onClick={() => handleEditorAction("italic")}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                title="Italic"
              >
                <IconItalic size={15} />
              </button>
              <button
                type="button"
                onClick={() => handleEditorAction("underline")}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                title="Underline"
              >
                <IconUnderline size={15} />
              </button>

              <span className="w-px h-5 bg-gray-200 mx-1"></span>

              {/* Text Alignments */}
              <button
                type="button"
                onClick={() => handleEditorAction("justifyLeft")}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                title="Align Left"
              >
                <IconAlignLeft size={15} />
              </button>
              <button
                type="button"
                onClick={() => handleEditorAction("justifyCenter")}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                title="Align Center"
              >
                <IconAlignCenter size={15} />
              </button>
              <button
                type="button"
                onClick={() => handleEditorAction("justifyRight")}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                title="Align Right"
              >
                <IconAlignRight size={15} />
              </button>
              <button
                type="button"
                onClick={() => handleEditorAction("justifyFull")}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                title="Justify"
              >
                <IconAlignJustified size={15} />
              </button>

              <span className="w-px h-5 bg-gray-200 mx-1"></span>

              {/* Lists and structures */}
              <button
                type="button"
                onClick={() => handleEditorAction("insertOrderedList")}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                title="Numbered List"
              >
                <IconListNumbers size={15} />
              </button>

              <button
                type="button"
                onClick={() => handleEditorAction("createLink", prompt("Enter URL:") || "")}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                title="Insert Link"
              >
                <IconLink size={15} />
              </button>

              <button
                type="button"
                onClick={() => handleEditorAction("insertImage", prompt("Enter Image URL:") || "")}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                title="Insert Image"
              >
                <IconPhoto size={15} />
              </button>

              <span className="w-px h-5 bg-gray-200 mx-1"></span>

              {/* Undo / Redo */}
              <button
                type="button"
                onClick={() => handleEditorAction("undo")}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                title="Undo"
              >
                <IconArrowBackUp size={15} />
              </button>
              <button
                type="button"
                onClick={() => handleEditorAction("redo")}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                title="Redo"
              >
                <IconArrowForwardUp size={15} />
              </button>

            </div>

            {/* Quick Template Indicator */}
            <div className="text-[10px] text-gray-400 font-mono font-bold flex items-center gap-1 bg-gray-50 px-2.5 py-1 rounded-lg border border-gray-100">
              <span className="w-1.5 h-1.5 bg-[#FBD037] rounded-full inline-block"></span>
              Saudi Arabia localized
            </div>
          </div>

          {/* Main Workspace Layout (Canvas left, Tabbed properties right) */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
            
            {/* Interactive Document Page Canvas (Span 3 cols) */}
            <div className="lg:col-span-3 bg-gray-100 border border-gray-200 rounded-2xl p-6 flex justify-center items-start min-h-[600px] overflow-x-auto relative">
              
              {/* Paper Canvas (representing A4 format sheet with borders/watermark) */}
              <div
                style={{ fontSize: `${fontSize}px` }}
                className={`bg-white shadow-xl relative transition-all duration-300 font-sans p-10 flex flex-col ${
                  paperSize === "A4" ? "w-[595px] min-h-[842px]" : "w-[841px] min-h-[1189px]"
                } ${propBorder ? "border-4 border-double border-gray-300" : "border border-gray-200"}`}
              >
                
                {/* 1. Letterhead block (Image 2 Properties "Add Letter Head") */}
                {propLetterHead && (
                  <div className="border-b border-[#FBD037] pb-3 mb-4 flex justify-between items-center text-[10px]">
                    <div className="text-left font-bold text-gray-800">
                      <div className="flex items-center gap-1 text-[11px] font-black tracking-wide text-gray-900">
                        <span className="w-2.5 h-2.5 bg-[#FBD037] rounded-md inline-block"></span>
                        GWARM LOGISTICS KSA
                      </div>
                      <span className="text-[7.5px] text-gray-400 font-medium">Riyadh Head Office, Kingdom of Saudi Arabia</span>
                    </div>
                    {editorLang === "bilingual" && (
                      <div className="text-right font-bold text-gray-800" dir="rtl">
                        <div className="text-[11px] font-black tracking-wide text-gray-900">
                          جوارم للخدمات اللوجستية بالمملكة
                        </div>
                        <span className="text-[7.5px] text-gray-400 font-medium">المكتب الرئيسي بالرياض، المملكة العربية السعودية</span>
                      </div>
                    )}
                  </div>
                )}

                {/* 2. Custom Title Header block ("Add Header") */}
                {propHeader && (
                  <div className="text-center font-bold text-[12px] uppercase underline text-gray-900 tracking-wider mb-4">
                    {editorLang === "bilingual" ? "خطاب رسمي / Official Letter" : editorLang === "arabic" ? "خطاب رسمي" : "Official Statement Letter"}
                  </div>
                )}

                {/* 3. Watermark Layer ("Enable Company Watermark") */}
                {propWatermark && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.06] select-none z-0">
                    <div className="text-center border-8 border-gray-900 p-8 rounded-full font-serif font-black text-6xl tracking-widest uppercase rotate-[-35deg]">
                      GWARM KSA
                    </div>
                  </div>
                )}

                {/* 4. Canvas Body Editors (interactive content editable) */}
                <div className="flex-grow z-10 grid gap-6 relative">
                  
                  {editorLang === "bilingual" ? (
                    /* SIDE-BY-SIDE Bilingual Columns Layout */
                    <div className="grid grid-cols-2 gap-6 min-h-[400px]">
                      
                      {/* Arabic Column (Left) */}
                      <div className="border-r border-dashed border-gray-200 pr-3 flex flex-col">
                        <div className="text-[9px] font-bold text-gray-400 mb-1 flex items-center gap-1 select-none">
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                          Arabic Template Column (RTL)
                        </div>
                        <div
                          ref={contentRefAr}
                          contentEditable
                          suppressContentEditableWarning
                          dir="rtl"
                          className="flex-grow outline-none border border-transparent focus:border-amber-200 rounded p-1 text-right leading-relaxed font-sans text-gray-800"
                          data-placeholder="أدخل نص القالب العربي هنا..."
                        >
                          <div className="font-bold text-[13px] mb-2">عزيزي الموظف،</div>
                          <div>يسعدنا إعلامك بأنه تم قبولك لوظيفة <strong>{"{{DESIGNATION}}"}</strong> بقسم <strong>{"{{DEPARTMENT}}"}</strong> بشركة جوارم للخدمات اللوجستية.</div>
                          <div className="mt-2">تاريخ مباشرتك المقررة للعمل هو <strong>{"{{JOINING_DATE}}"}</strong> وتخضع لفترة تجربة منصوص عليها بالنظام.</div>
                        </div>
                      </div>

                      {/* English Column (Right) */}
                      <div className="flex flex-col">
                        <div className="text-[9px] font-bold text-gray-400 mb-1 flex items-center gap-1 select-none">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                          English Template Column (LTR)
                        </div>
                        <div
                          ref={contentRefEng}
                          contentEditable
                          suppressContentEditableWarning
                          className="flex-grow outline-none border border-transparent focus:border-amber-200 rounded p-1 text-left leading-relaxed font-serif text-gray-850"
                          data-placeholder="Enter English template text here..."
                        >
                          <div className="font-bold text-[13px] mb-2">Dear Candidate,</div>
                          <div>We are pleased to offer you employment for the position of <strong>{"{{DESIGNATION}}"}</strong> in the <strong>{"{{DEPARTMENT}}"}</strong> department with GWARM Logistics.</div>
                          <div className="mt-2">Your joining date will be <strong>{"{{JOINING_DATE}}"}</strong>, subject to successful background verification check.</div>
                        </div>
                      </div>

                    </div>
                  ) : editorLang === "arabic" ? (
                    /* Monolingual Arabic Column */
                    <div className="flex flex-col min-h-[400px]">
                      <div className="text-[9px] font-bold text-gray-400 mb-1 flex items-center gap-1 select-none">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                        Arabic (RTL) Editor
                      </div>
                      <div
                        ref={contentRefAr}
                        contentEditable
                        suppressContentEditableWarning
                        dir="rtl"
                        className="flex-grow outline-none border border-transparent focus:border-amber-200 rounded p-1 text-right leading-relaxed font-sans text-gray-800"
                        data-placeholder="أدخل نص القالب هنا..."
                      >
                        <div className="font-bold text-[13px] mb-2">عزيزي الموظف،</div>
                        <div>يسعدنا إعلامك بأنه تم قبولك لوظيفة <strong>{"{{DESIGNATION}}"}</strong> بقسم <strong>{"{{DEPARTMENT}}"}</strong>.</div>
                      </div>
                    </div>
                  ) : (
                    /* Monolingual English Column */
                    <div className="flex flex-col min-h-[400px]">
                      <div className="text-[9px] font-bold text-gray-400 mb-1 flex items-center gap-1 select-none">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        English (LTR) Editor
                      </div>
                      <div
                        ref={contentRefEng}
                        contentEditable
                        suppressContentEditableWarning
                        className="flex-grow outline-none border border-transparent focus:border-amber-200 rounded p-1 text-left leading-relaxed font-serif text-gray-850"
                        data-placeholder="Enter template text here..."
                      >
                        <div className="font-bold text-[13px] mb-2">Dear Candidate,</div>
                        <div>We are pleased to offer you employment for the position of <strong>{"{{DESIGNATION}}"}</strong> in the <strong>{"{{DEPARTMENT}}"}</strong>.</div>
                      </div>
                    </div>
                  )}

                </div>

                {/* 5. Custom Signature Lines Block ("Add Signature Lines") */}
                {propSignature && (
                  <div className="border-t border-gray-100 pt-5 mt-6 flex justify-between items-end text-[9px] z-10">
                    <div className="text-left">
                      <div className="font-bold text-gray-850">HR Authorization Department</div>
                      <div className="text-gray-400 text-[7px] font-mono">DIGISIGN_REF_KSA_9912</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-gray-850">Candidate Acceptance Signature</div>
                      <div className="text-gray-400 text-[7px] font-mono">Date: ____________________</div>
                    </div>
                  </div>
                )}

                {/* 6. Footer Block ("Enable Footer") */}
                {propFooter && (
                  <div className="border-t border-gray-100 pt-2 mt-4 text-[7px] text-gray-400 text-center font-mono z-10 flex justify-between items-center select-none">
                    <span>Confidential - GWARM Logistics Co.</span>
                    <span>Page 1 of 1</span>
                    <span>Riyadh, KSA</span>
                  </div>
                )}

              </div>
            </div>

            {/* Config & Properties/Variables Right Sidebar */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-xs overflow-hidden flex flex-col h-[600px]">
              
              {/* Tab Selector representing mockup Right Sidebar */}
              <div className="grid grid-cols-2 border-b border-gray-200 bg-gray-50 p-1">
                <button
                  onClick={() => setEditorRightTab("properties")}
                  className={`py-2 text-xs font-bold rounded-lg transition ${
                    editorRightTab === "properties"
                      ? "bg-white text-gray-900 shadow-xs"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Properties
                </button>
                <button
                  onClick={() => setEditorRightTab("variables")}
                  className={`py-2 text-xs font-bold rounded-lg transition ${
                    editorRightTab === "variables"
                      ? "bg-white text-gray-900 shadow-xs"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Variables
                </button>
              </div>

              {/* Scrollable Sidebar Panel Content */}
              <div className="flex-grow p-4 overflow-y-auto text-xs space-y-4">
                
                {/* properties Tab View (Image 2) */}
                {editorRightTab === "properties" && (
                  <div className="space-y-4">
                    
                    {/* Language Settings (Saudi localization bilingual options) */}
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Contract Language Style</label>
                      <select
                        value={editorLang}
                        onChange={e => setEditorLang(e.target.value as any)}
                        className="w-full p-2 border border-gray-200 bg-white rounded-xl text-xs font-bold outline-none focus:border-[#FBD037]"
                      >
                        <option value="english">English Only</option>
                        <option value="arabic">Arabic Only (العربية)</option>
                        <option value="bilingual">Bilingual (العربية & English)</option>
                      </select>
                    </div>

                    <span className="block border-b border-gray-100 my-2"></span>

                    {/* Checkboxes matching Image 2 */}
                    <div className="space-y-3 font-semibold text-gray-700">
                      
                      <label className="flex items-center gap-2 cursor-pointer hover:text-gray-900">
                        <input
                          type="checkbox"
                          checked={propSignature}
                          onChange={e => setPropSignature(e.target.checked)}
                          className="w-4 h-4 accent-[#FBD037] rounded border-gray-300"
                        />
                        <span>Add Signature Lines</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer hover:text-gray-900">
                        <input
                          type="checkbox"
                          checked={propWatermark}
                          onChange={e => setPropWatermark(e.target.checked)}
                          className="w-4 h-4 accent-[#FBD037] rounded border-gray-300"
                        />
                        <span>Enable Company Watermark</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer hover:text-gray-900">
                        <input
                          type="checkbox"
                          checked={propBorder}
                          onChange={e => setPropBorder(e.target.checked)}
                          className="w-4 h-4 accent-[#FBD037] rounded border-gray-300"
                        />
                        <span>Add Border</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer hover:text-gray-900">
                        <input
                          type="checkbox"
                          checked={propHeader}
                          onChange={e => setPropHeader(e.target.checked)}
                          className="w-4 h-4 accent-[#FBD037] rounded border-gray-300"
                        />
                        <span>Add Header</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer hover:text-gray-900">
                        <input
                          type="checkbox"
                          checked={propLetterHead}
                          onChange={e => setPropLetterHead(e.target.checked)}
                          className="w-4 h-4 accent-[#FBD037] rounded border-gray-300"
                        />
                        <span>Add Letter Head</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer hover:text-gray-900">
                        <input
                          type="checkbox"
                          checked={propFooter}
                          onChange={e => setPropFooter(e.target.checked)}
                          className="w-4 h-4 accent-[#FBD037] rounded border-gray-300"
                        />
                        <span>Enable Footer</span>
                      </label>

                    </div>

                    {/* Paper specs summary */}
                    <div className="bg-amber-50 border border-amber-100 p-3 rounded-xl text-[10px] text-amber-800 leading-normal mt-6 font-medium">
                      <strong>Protip:</strong> Use the double curly braces syntax (e.g. <code>{"{{NAME}}"}</code>) to automatically bind database candidate parameters to the final letter document.
                    </div>
                  </div>
                )}

                {/* Variables Tab View (Image 3) */}
                {editorRightTab === "variables" && (
                  <div className="space-y-3 flex flex-col h-full">
                    
                    {/* Variables Search */}
                    <div className="relative mb-2">
                      <IconSearch size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search variables..."
                        value={searchVar}
                        onChange={e => setSearchVar(e.target.value)}
                        className="pl-8 pr-3 py-1.5 w-full rounded-lg border border-gray-200 bg-white text-xs outline-none focus:border-[#FBD037]"
                      />
                    </div>

                    {/* Scrollable list of variables */}
                    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                      {filteredVars.map(v => (
                        <div
                          key={v.name}
                          className="flex items-center justify-between p-2 border border-gray-100 bg-gray-50 rounded-xl hover:border-amber-200 hover:bg-amber-50/20 transition group"
                        >
                          <div className="truncate pr-2">
                            <span className="font-mono text-[10.5px] font-black text-gray-800 block">
                              {"{" + "{" + v.name + "}" + "}"}
                            </span>
                            <span className="text-[9px] text-gray-400 block truncate">{v.desc}</span>
                          </div>
                          <button
                            onClick={() => handleCopyVariable(v.name)}
                            className="p-1 rounded-md border border-gray-200 bg-white text-gray-500 hover:text-gray-800 hover:border-gray-300 transition"
                            title="Copy placeholder variable code"
                          >
                            <IconCopy size={12} />
                          </button>
                        </div>
                      ))}
                    </div>

                  </div>
                )}

              </div>
            </div>

          </div>

        </div>
      )}

    </div>
  );
}
